import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

export type TicTacToeCell = PlayerIndex | null;

export interface TicTacToeState extends BaseState {
	kind: "tictactoe";
	board: TicTacToeCell[];
	/** The three squares that won, so the board can highlight them. */
	winningLine: number[] | null;
}

export interface TicTacToeMove {
	square: number;
}

const LINES: readonly (readonly [number, number, number])[] = [
	[0, 1, 2],
	[3, 4, 5],
	[6, 7, 8],
	[0, 3, 6],
	[1, 4, 7],
	[2, 5, 8],
	[0, 4, 8],
	[2, 4, 6],
];

function findWin(board: TicTacToeCell[]): { winner: PlayerIndex; line: number[] } | null {
	for (const line of LINES) {
		const [a, b, c] = line;
		const first = board[a];
		if (first !== null && first === board[b] && first === board[c]) {
			return { winner: first, line: [a, b, c] };
		}
	}
	return null;
}

export const ticTacToe: GameDefinition<TicTacToeState, TicTacToeMove> = {
	id: "tictactoe",
	category: "classics",
	title: "Tic-Tac-Toe",
	emoji: "❌",
	blurb: "Three in a row wins. Quick games, sneaky traps.",
	howTo: [
		"Take turns tapping an empty square.",
		"Get three of your marks in a row — across, down or diagonal.",
		"If the board fills up with nobody in a row, it's a draw.",
	],

	create() {
		return {
			kind: "tictactoe",
			board: Array<TicTacToeCell>(9).fill(null),
			turn: 0,
			scores: [0, 0],
			result: null,
			winningLine: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const square = (raw as { square?: unknown }).square;
		if (typeof square !== "number" || !Number.isInteger(square)) return null;
		if (square < 0 || square > 8) return null;
		return { square };
	},

	apply(state, player, move): MoveOutcome<TicTacToeState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.board[move.square] !== null) return reject("That square is taken.");

		const board = [...state.board];
		board[move.square] = player;

		const win = findWin(board);
		if (win) {
			const scores: [number, number] = [0, 0];
			scores[win.winner] = 1;
			return {
				ok: true,
				state: {
					...state,
					board,
					scores,
					turn: null,
					result: { winner: win.winner },
					winningLine: win.line,
				},
			};
		}

		if (board.every((cell) => cell !== null)) {
			return {
				ok: true,
				state: { ...state, board, turn: null, result: { winner: null } },
			};
		}

		return { ok: true, state: { ...state, board, turn: otherPlayer(player) } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		return state.board
			.map((cell, square) => (cell === null ? { square } : null))
			.filter((move): move is TicTacToeMove => move !== null);
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};
